/**
 * Official vs proposed setlist.
 * Notes stay live. Order, BPM, titles, and add/remove need setlist-moderator (or tour owner) approval.
 */
import { parseSetlistText, serializeSetlistDoc } from '../components/SetlistEditor';

export const HAWK_TOUR_IDS = ['tdemo_mtnwest26'];
export const HAWK_BPM_CYCLE = [118, 92, 140, 76, 128, 104, 160, 88, 112, 134, 96, 148];

const BPM_IN_NOTES_RE = /\bbpm\s*:?\s*(\d{2,3})\b/i;

export function normalizeBpm(value) {
  if (value == null || value === '') return null;
  const n = Number(String(value).replace(/[^\d.]/g, ''));
  if (!Number.isFinite(n) || n < 40 || n > 300) return null;
  return Math.round(n);
}

/** Digits only, max 3 — keep this in the field while typing. */
export function parseBpmInputDraft(text) {
  return String(text || '').replace(/[^\d]/g, '').slice(0, 3);
}

/**
 * Commit a typed BPM. Empty clears. Incomplete (e.g. "1") keeps the previous value
 * so mid-keystroke digits are not thrown away.
 */
export function commitBpmInput(draft, previous = null) {
  const trimmed = parseBpmInputDraft(draft);
  if (!trimmed) return null;
  const next = normalizeBpm(trimmed);
  if (next != null) return next;
  return normalizeBpm(previous);
}

export function bpmFromNotes(song) {
  const blob = (song?.lines || [])
    .map((line) => (line.fields || []).join(' '))
    .join(' ');
  const match = blob.match(BPM_IN_NOTES_RE);
  return match ? normalizeBpm(match[1]) : null;
}

export function songBpm(song) {
  return normalizeBpm(song?.bpm) || bpmFromNotes(song);
}

export function parseSetlistDoc(raw) {
  const parsed = parseSetlistText(raw);
  return {
    songs: parsed.songs || [],
    pending: normalizePending(parsed.pending),
  };
}

export function normalizePending(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const songs = Array.isArray(raw.songs) ? raw.songs : null;
  if (!songs) return null;
  const byId = String(raw.byId || raw.by || '').trim();
  return {
    byId,
    byName: String(raw.byName || '').trim(),
    at: String(raw.at || '').trim(),
    songs,
  };
}

export function serializeSetlistDocWithPending({ songs, pending } = {}) {
  return serializeSetlistDoc({ songs: songs || [], pending: pending || null });
}

export function officialSetlistRaw(raw) {
  const { songs } = parseSetlistDoc(raw);
  return serializeSetlistDoc({ songs });
}

export function isHawkDemoTour(tourId, tourName) {
  const id = String(tourId || '').trim().toLowerCase();
  if (HAWK_TOUR_IDS.includes(id)) return true;
  const name = String(tourName || '').toLowerCase();
  return name.includes('hawk') && (name.includes('mountain west') || name.includes('mtn west'));
}

export function seedHawkSongBpms(songs) {
  return (songs || []).map((song, index) => {
    if (songBpm(song)) return { ...song, bpm: songBpm(song) };
    return { ...song, bpm: HAWK_BPM_CYCLE[index % HAWK_BPM_CYCLE.length] };
  });
}

export function needsHawkBpmSeed(raw, tourId, tourName) {
  if (!isHawkDemoTour(tourId, tourName)) return false;
  const { songs } = parseSetlistDoc(raw);
  if (!songs.length) return false;
  return songs.some((song) => !songBpm(song));
}

function songId(song, index) {
  return String(song?.id || `idx-${index}`);
}

function sharedSignature(songs) {
  return (songs || [])
    .map((song, index) => `${songId(song, index)}|${String(song?.title || '').trim()}|${songBpm(song) || ''}|${String(song?.duration || '').trim()}`)
    .join('~');
}

export function sharedSetlistFieldsChanged(aSongs, bSongs) {
  return sharedSignature(aSongs) !== sharedSignature(bSongs);
}

function mergeLiveNotes(published, next) {
  const byId = new Map((next || []).map((song, index) => [songId(song, index), song]));
  return (published || []).map((song, index) => {
    const incoming = byId.get(songId(song, index));
    if (!incoming) return song;
    return { ...song, lines: incoming.lines || [] };
  });
}

export function applySetlistEditorChange({
  currentRaw,
  nextSongs,
  actorId,
  actorName,
  canPublish,
}) {
  const current = parseSetlistDoc(currentRaw);
  const next = Array.isArray(nextSongs) ? nextSongs : [];
  if (canPublish) {
    return serializeSetlistDocWithPending({ songs: next, pending: null });
  }
  const publishedWithNotes = mergeLiveNotes(current.songs, next);
  if (!sharedSetlistFieldsChanged(publishedWithNotes, next)) {
    return serializeSetlistDocWithPending({ songs: publishedWithNotes, pending: current.pending });
  }
  return serializeSetlistDocWithPending({
    songs: publishedWithNotes,
    pending: {
      byId: String(actorId || '').trim(),
      byName: String(actorName || '').trim(),
      at: new Date().toISOString(),
      songs: next,
    },
  });
}

export function acceptSetlistPending(raw) {
  const current = parseSetlistDoc(raw);
  if (!current.pending?.songs) return serializeSetlistDocWithPending({ songs: current.songs, pending: null });
  return serializeSetlistDocWithPending({ songs: current.pending.songs, pending: null });
}

export function rejectSetlistPending(raw) {
  const current = parseSetlistDoc(raw);
  return serializeSetlistDocWithPending({ songs: current.songs, pending: null });
}

export function setlistDisplaySongs(raw) {
  const current = parseSetlistDoc(raw);
  return current.pending?.songs?.length ? current.pending.songs : current.songs;
}

export function setlistHighlightMap(raw) {
  const current = parseSetlistDoc(raw);
  const pendingSongs = current.pending?.songs;
  if (!pendingSongs?.length) return {};
  const publishedIndex = new Map((current.songs || []).map((song, index) => [songId(song, index), index]));
  const map = {};
  pendingSongs.forEach((song, index) => {
    const id = songId(song, index);
    const pubIndex = publishedIndex.has(id) ? publishedIndex.get(id) : -1;
    const published = pubIndex >= 0 ? current.songs[pubIndex] : null;
    const reasons = [];
    if (!published) reasons.push('added');
    else {
      if (pubIndex !== index) reasons.push('order');
      if (songBpm(published) !== songBpm(song)) reasons.push('bpm');
      if (String(published.duration || '').trim() !== String(song.duration || '').trim()) reasons.push('time');
      if (String(published.title || '').trim() !== String(song.title || '').trim()) reasons.push('title');
    }
    if (reasons.length) map[id] = reasons;
  });
  return map;
}